import React, { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Backdrop
} from "@mui/material"
import AppPagination from "components/StyledComponents/AppPagination"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { useDispatch, useSelector } from "react-redux"
import { useTheme } from "@mui/system"
import CategoryForm from "./components/CategoryForm"
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"
import {
  createCategoryAction,
  deleteCategoryAction,
  getCategoriesAction,
  updateCategoryAction
} from "redux/superAdmin/actions"
import { categoryColumns } from "helpers/constant"
import { Formik } from "formik"
import { validationSchemaCategory } from "helpers/validationSchema"
import { formatDates } from "helpers/functions"

const StatusText = ({ active }) => (
  <Typography
    variant="caption3"
    sx={{
      color: active ? "#4CAF50" : "#F44336",
      fontFamily: "Inter-Regular",
      fontWeight: 600
    }}>
    {active ? "Active" : "Inactive"}
  </Typography>
)

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: "12px 24px",
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}))

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "16px 24px 16px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": {
      paddingRight: 0
    }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      paddingRight: 0
    }
  }
}))

const ActionsMenu = ({ onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const openMenu = Boolean(anchorEl)
  const theme = useTheme()

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ color: "text.secondary" }}>
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Backdrop
        sx={{ color: theme.palette.white.main, zIndex: 1300 }}
        open={openMenu}
        onClick={handleMenuClose}>
        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
          PaperProps={{
            sx: {
              marginTop: "15px",
              backgroundColor: theme.palette.background.default,
              borderRadius: "12px",
              "& .MuiList-root": {
                backgroundColor: theme.palette.background.default,
                width: "150px",
                boxSizing: "border-box",
                padding: "10px 0"
              }
            }
          }}>
          <StyledMenuItem
            onClick={() => {
              handleMenuClose()
              onEdit()
            }}>
            <Typography variant="body1Regular">Edit</Typography>
          </StyledMenuItem>
          <StyledMenuItem
            onClick={() => {
              handleMenuClose()
              onDelete()
            }}>
            <Typography sx={{ color: theme.palette.red.main }} variant="body1Regular">
              Delete
            </Typography>
          </StyledMenuItem>
        </Menu>
      </Backdrop>
    </>
  )
}

const CategoryManagement = ({ addTrigger = 0 }) => {
  const { categories } = useSelector((state) => state.superAdmin)
  const dispatch = useDispatch()
  const [formOpen, setFormOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [isEdit, setIsEdit] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    dispatch(getCategoriesAction())
    document.title = "Categories"
    setCurrentPage(1)
  }, [])

  useEffect(() => {
    if (addTrigger > 0) {
      setSelectedCategory(null)
      setIsEdit(false)
      setFormOpen(true)
    }
  }, [addTrigger])

  const initialValues = {
    name: "",
    slug: "",
    description: "",
    is_active: true
  }

  const initialValuesEdit = {
    name: selectedCategory?.name,
    slug: selectedCategory?.slug,
    description: selectedCategory?.description,
    is_active: selectedCategory?.is_active
  }

  const handleFormSave = (formData) => {
    const action = selectedCategory?.id ? updateCategoryAction : createCategoryAction
    const params = selectedCategory?.id
      ? { categoryId: selectedCategory.id, payload: formData, callback: onSaveSuccess }
      : { payload: formData, callback: onSaveSuccess }

    dispatch(action(params))
  }

  const onSaveSuccess = () => {
    setFormOpen(false)
    setSelectedCategory(null)
    setIsEdit(false)
    // dispatch(getCategoriesAction())
  }

  const handleConfirmDelete = () => {
    dispatch(
      deleteCategoryAction({
        categoryId: categoryToDelete.id,
        callback: () => {
          setDeleteDialogOpen(false)
          setCategoryToDelete(null)
          // loadCategories()
        }
      })
    )
  }

  const itemsPerPage = 12
  const totalPages = Math.ceil((categories?.length || 0) / itemsPerPage)

  const paginatedData = categories?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  return (
    <Box>
      {paginatedData?.length === 0 ? (
        <Box className="d-flex align-items justify-content-center">
          <Typography variant="body2Regular" color="textSecondary" sx={{ textAlign: "center" }}>
            No categories configured yet. Use &quot;Add New Category&quot; to create one.
          </Typography>
        </Box>
      ) : (
        <Paper sx={{ mt: 3 }}>
          <TableContainer sx={{ maxHeight: 700 }}>
            <CustomTable stickyHeader>
              <TableHead>
                <TableRow>
                  {categoryColumns?.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column?.align}
                      sx={{ minWidth: column?.minWidth }}>
                      <Typography variant="body2Regular" style={{ opacity: 0.6 }}>
                        {column.label}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData?.map((cat) => (
                  <TableRow
                    key={cat.id}
                    sx={!cat.is_active ? { opacity: "0.6" } : { opacity: "1" }}>
                    <TableCell>
                      <Typography variant="body2Regular">{cat.name}</Typography>
                    </TableCell>
                    {/* <TableCell>
                      <Typography variant="body2Regular">{cat.slug}</Typography>
                    </TableCell> */}
                    <TableCell>
                      <Typography variant="body2Regular">
                        {cat.gift_cards_count !== undefined ? cat.gift_cards_count : "-"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <StatusText active={cat.is_active} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2Regular">
                        {cat.created_at ? formatDates(cat.created_at) : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <ActionsMenu
                        onEdit={() => {
                          setSelectedCategory(cat)
                          setIsEdit(true)
                          setFormOpen(true)
                        }}
                        onDelete={() => {
                          setCategoryToDelete(cat)
                          setDeleteDialogOpen(true)
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </CustomTable>
          </TableContainer>
          <AppPagination
            onPageChange={setCurrentPage}
            totalPages={totalPages}
            currentPage={currentPage}
          />
        </Paper>
      )}

      {formOpen && (
        <Formik
          onSubmit={handleFormSave}
          validationSchema={validationSchemaCategory}
          initialValues={selectedCategory ? initialValuesEdit : initialValues}
          enableReinitialize>
          {(formik) => (
            <CategoryForm
              open={formOpen}
              onClose={() => {
                setFormOpen(false)
                setSelectedCategory(null)
                setIsEdit(false)
              }}
              formik={formik}
              isEdit={isEdit}
            />
          )}
        </Formik>
      )}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setCategoryToDelete(null)
        }}
        onDelete={handleConfirmDelete}
        title="Delete Category"
        description={`Are you sure you want to delete the "${categoryToDelete?.name}" category? This cannot be undone.`}
        btnText="Delete"
      />
    </Box>
  )
}

export default CategoryManagement
